import { useContext, useEffect, useState } from 'react';
import firestore from '../../firestore';
import firebase from 'firebase/compat/app';
import Firebase from '../../firebase_setup';
import {
	Calendar,
	CheckCircle,
	XCircle,
	Clock,
	Plus,
	Pencil,
	Save,
	Trash2,
	Search,
	Filter,
	Image as ImageIcon,
	Link as LinkIcon,
	Globe,
	Users,
	Upload,
	Loader
} from 'lucide-react';
import { ContainerSpinner } from '../../components/Spinner';
import { Dialog } from '../../components/Dialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import Input from '../../components/Input';
import Select from '../../components/Select';
import { Button } from '../../shadcn/Button';
import { Checkbox } from '../../shadcn/Checkbox';
import { DateRangePicker } from '../../shadcn/DateRangePicker';
import { type DateRange } from 'react-day-picker';
import { GlobalContext } from '../../context/GlobalContext';
import { COUNTRIES, COUNTRY_LABELS, LEVELS } from '../../utils/constants';
import { ucfirst } from '../../utils/formatters';

// Type definitions
export interface MarketingCampaign {
	id?: string;
	type: 'ON_STARTUP';
	campaign_window?: {
		from: {
			seconds: number;
			nanoseconds: number;
		};
		to: {
			seconds: number;
			nanoseconds: number;
		};
	};
	title?: string;
	image_url: string;
	redirect_url?: string;
	target: {
		countries: string[];
		exclude_levels?: (typeof LEVELS)[number][];
		platforms: ['mobile'];
	};
	is_active?: boolean;
}

// Format date
const formatDate = (timestamp: { seconds: number; nanoseconds: number }) => {
	return new Date(timestamp.seconds * 1000).toLocaleDateString();
};

// Check if campaign is active based on dates
const getCampaignStatus = (campaign: MarketingCampaign) => {
	const now = Date.now() / 1000;

	if (!campaign.is_active) return 'inactive';
	if (!campaign.campaign_window) return 'inactive';
	if (now < campaign.campaign_window.from.seconds) return 'scheduled';
	if (now > campaign.campaign_window.to.seconds) return 'expired';
	return 'active';
};

// Edit Dialog Component
function EditCampaignDialog({
	campaign,
	onClose,
	onSave,
	onDelete,
	saving,
	deleting,
	isNew
}: {
	campaign: MarketingCampaign;
	onClose: () => void;
	onSave: (updatedCampaign: MarketingCampaign, originalId?: string) => void;
	onDelete?: (campaignId: string) => void;
	saving: boolean;
	deleting?: boolean;
	isNew?: boolean;
}) {
	const [editedCampaign, setEditedCampaign] = useState<MarketingCampaign>({ ...campaign });
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);

	const handleSave = () => {
		// Validate required fields
		if (!editedCampaign.image_url?.trim()) {
			setAlertDialog({ title: 'Error', message: 'Debe subir una imagen para la campaña' });
			return;
		}

		if (uploadingImage) {
			setAlertDialog({ title: 'Error', message: 'Por favor espere a que termine de subir la imagen' });
			return;
		}

		if (
			!editedCampaign.campaign_window ||
			!editedCampaign.campaign_window.from ||
			!editedCampaign.campaign_window.to ||
			!editedCampaign.campaign_window.from.seconds ||
			!editedCampaign.campaign_window.to.seconds
		) {
			setAlertDialog({ title: 'Error', message: 'El período de campaña es requerido' });
			return;
		}

		if (!editedCampaign.target?.countries || editedCampaign.target.countries.length === 0) {
			setAlertDialog({ title: 'Error', message: 'Debe seleccionar al menos un país' });
			return;
		}

		console.log('Saving campaign:', editedCampaign);
		onSave(editedCampaign, campaign.id);
	};

	const handleDelete = () => {
		setShowDeleteConfirm(true);
	};

	const handleConfirmDelete = async (): Promise<true | string> => {
		if (onDelete && campaign.id) {
			onDelete(campaign.id);
			return true;
		}
		return true;
	};

	const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (uploadingImage) return;

		const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
		const REQUIRED_ASPECT_RATIO = 9 / 16; // 0.5625
		const ASPECT_RATIO_TOLERANCE = 0.025; // Allow 1% tolerance

		try {
			setUploadingImage(true);
			setUploadProgress(0);

			const file = e.target.files?.[0];
			if (!file) throw new Error('No file selected');

			const isValidImage = file.size && IMAGE_TYPES.includes(file.type);
			const extension = file.name.split('.')?.pop()?.toLowerCase();
			const size = file.size;

			if (!isValidImage || !extension) {
				setAlertDialog({
					title: 'Error',
					message: 'Debe subir una imagen válida (JPG, JPEG, PNG o WebP)'
				});
				throw new Error('Invalid image type');
			} else if (size > 5000000) {
				setAlertDialog({
					title: 'Error',
					message: 'El tamaño de la imagen no puede superar los 5MB'
				});
				throw new Error('File too large');
			}

			// Validate aspect ratio by loading the image
			const imageValidation = await new Promise<{ valid: boolean; aspectRatio?: number }>((resolve) => {
				const img = new Image();
				const objectUrl = URL.createObjectURL(file);
				
				img.onload = () => {
					const aspectRatio = img.width / img.height;
					const isValid = Math.abs(aspectRatio - REQUIRED_ASPECT_RATIO) <= ASPECT_RATIO_TOLERANCE;
					URL.revokeObjectURL(objectUrl); // Clean up memory
					resolve({ valid: isValid, aspectRatio });
				};
				img.onerror = () => {
					URL.revokeObjectURL(objectUrl); // Clean up memory
					resolve({ valid: false });
				};
				img.src = objectUrl;
			});

			if (!imageValidation.valid) {
				setAlertDialog({
					title: 'Error',
					message: `La imagen debe tener una proporción de aspecto de 9:16 (por ejemplo dimensiones 1080x1920). La imagen seleccionada no califica.`
				});
				throw new Error('Invalid aspect ratio');
			}

			const storage = Firebase.storage();
			const storageRef = storage.ref();
			const fileName = `marketing_campaigns/${Date.now()}_${file.name}`;
			const fileRef = storageRef.child(fileName);

			const uploadTask = fileRef.put(file, {
				cacheControl: 'public,max-age=31536000,immutable'
			});

			// Monitor upload progress
			uploadTask.on(
				'state_changed',
				(snapshot) => {
					const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					setUploadProgress(progress);
				},
				(error) => {
					console.error('Upload error:', error);
					setAlertDialog({
						title: 'Error',
						message: 'Error al subir la imagen. Por favor, intente nuevamente.'
					});
					setUploadingImage(false);
				},
				async () => {
					// Upload completed successfully
					const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
					if (downloadURL) {
						setEditedCampaign((prev) => ({ ...prev, image_url: downloadURL }));
					} else {
						throw new Error('Failed to get download URL');
					}
					setUploadingImage(false);
					setUploadProgress(0);
					// Reset file input to allow re-uploading the same file
					e.target.value = '';
				}
			);
		} catch (error) {
			console.error('Error uploading image:', error);
			setUploadingImage(false);
			setUploadProgress(0);
			// Reset file input
			e.target.value = '';
		}
	};

	const toggleCountry = (country: (typeof COUNTRIES)[number]) => {
		setEditedCampaign((prev) => {
			const currentCountries = prev.target?.countries || [];
			if (currentCountries.includes(country)) {
				return {
					...prev,
					target: {
						...prev.target,
						countries: currentCountries.filter((c) => c !== country)
					}
				};
			} else {
				return {
					...prev,
					target: {
						...prev.target,
						countries: [...currentCountries, country]
					}
				};
			}
		});
	};

	const toggleExcludeLevel = (level: (typeof LEVELS)[number]) => {
		setEditedCampaign((prev) => {
			const currentLevels = prev.target?.exclude_levels || [];
			if (currentLevels.includes(level)) {
				return {
					...prev,
					target: {
						...prev.target,
						exclude_levels: currentLevels.filter((l) => l !== level)
					}
				};
			} else {
				return {
					...prev,
					target: {
						...prev.target,
						exclude_levels: [...currentLevels, level]
					}
				};
			}
		});
	};

	const levelLabels: Record<(typeof LEVELS)[number], string> = {
		level_1: 'Krece Azul',
		level_2: 'Krece Plata',
		level_3: 'Krece Oro',
		level_4: 'Krece Platino'
	};

	return (
		<>
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable={true}>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}
			{showDeleteConfirm && (
				<ConfirmDialog
					title="Eliminar Campaña"
					message="¿Estás seguro de que deseas eliminar esta campaña? Esta acción no se puede deshacer."
					onConfirm={handleConfirmDelete}
					onClose={() => setShowDeleteConfirm(false)}
				/>
			)}
			<Dialog title="" onClose={onClose} closable={!saving} cerrarButton={false} className="!w-[min(700px,95%)] !p-2 md:!p-6">
				<div className="space-y-8 p-1 pb-20 md:pb-16">
					{/* Basic Information */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-primary-500/10 p-2">
								<ImageIcon className="h-5 w-5 text-primary-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Información de la Campaña</h3>
						</div>

						<div className="grid gap-4">
							<Select
								floatingLabel="Estado"
								options={[
									{ label: 'Activa', value: true },
									{ label: 'Inactiva', value: false }
								]}
								defaultValue={editedCampaign.is_active !== false}
								onSelectedChange={(value) => setEditedCampaign((prev) => ({ ...prev, is_active: value as boolean }))}
							/>

						<div className="space-y-1">
							<Input
								floatingLabel="Título del popup (opcional)"
								placeholder="Ej: ¡Oferta Especial!"
								value={editedCampaign.title || ''}
								onChange={(value) => setEditedCampaign((prev) => ({ ...prev, title: value }))}
							/>
							<p className="text-xs text-amber-400">
								Este título aparecerá en la parte superior del popup en la aplicación móvil
							</p>
							<p className="text-xs text-amber-400">
								Dejar vacío si la imagen es suficiente para comunicar la oferta
							</p>
						</div>

							{/* Image Upload */}
							<div className="space-y-3">
								<label className="block">
									<span className="text-sm font-medium text-neutral-200">Imagen de la campaña * (9:16)</span>
									<div className="mt-2">
										<input
											type="file"
											accept="image/jpeg,image/jpg,image/png,image/webp"
											onChange={handleImageUpload}
											disabled={uploadingImage || saving}
											className="hidden"
											id="campaign-image-upload"
										/>
										<label
											htmlFor="campaign-image-upload"
											className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-700 bg-neutral-900/30 p-6 transition-all hover:border-primary-500 hover:bg-neutral-900/50 ${
												uploadingImage ? 'pointer-events-none opacity-60' : ''
											}`}
										>
											{uploadingImage ? (
												<>
													<Loader className="mb-3 h-8 w-8 animate-spin text-primary-500" />
													<p className="text-sm font-medium text-neutral-300">Subiendo imagen...</p>
													<div className="mt-3 h-2 w-full max-w-xs overflow-hidden rounded-full bg-neutral-800">
														<div
															className="h-full bg-primary-500 transition-all duration-300"
															style={{ width: `${uploadProgress}%` }}
														/>
													</div>
													<p className="mt-2 text-xs text-neutral-400">{Math.round(uploadProgress)}%</p>
												</>
											) : editedCampaign.image_url ? (
												<>
													<CheckCircle className="mb-3 h-8 w-8 text-green-500" />
													<p className="text-sm font-medium text-neutral-300">Imagen cargada</p>
													<p className="mt-1 text-xs text-neutral-400">Click para cambiar la imagen</p>
												</>
											) : (
												<>
													<Upload className="mb-3 h-8 w-8 text-neutral-400" />
													<p className="text-sm font-medium text-neutral-300">Subir imagen</p>
													<p className="mt-1 text-xs text-neutral-400">JPG, JPEG, PNG o WebP (máx. 5MB)</p>
													<p className="mt-1 text-xs text-amber-400">Proporción requerida: 9:16 (ej: 1080x1920)</p>
												</>
											)}
										</label>
									</div>
								</label>

								{/* Image Preview */}
								{editedCampaign.image_url && !uploadingImage && (
									<div className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-4">
										<div className="mb-2 flex items-center justify-between">
											<p className="text-xs font-medium text-neutral-400">Vista previa:</p>
											<button
												onClick={() => setEditedCampaign((prev) => ({ ...prev, image_url: '' }))}
												className="rounded-lg bg-red-500/10 px-3 py-1 text-xs text-red-400 transition-colors hover:bg-red-500/20"
											>
												Eliminar imagen
											</button>
										</div>
										<img
											src={editedCampaign.image_url}
											alt="Preview"
											className="max-h-48 w-full rounded-lg object-contain"
											onError={(e) => {
												(e.target as HTMLImageElement).style.display = 'none';
											}}
										/>
									</div>
								)}
							</div>

							<Input
								floatingLabel="URL de redirección (opcional)"
								placeholder="https://ejemplo.com/destino"
								value={editedCampaign.redirect_url || ''}
								onChange={(value) => setEditedCampaign((prev) => ({ ...prev, redirect_url: value }))}
							/>

							<DateRangePicker
								defaultValue={
									editedCampaign.campaign_window?.from && editedCampaign.campaign_window?.to
										? {
												from: new Date(editedCampaign.campaign_window.from.seconds * 1000),
												to: new Date(editedCampaign.campaign_window.to.seconds * 1000)
											}
										: undefined
								}
								floatingLabel="Período de campaña *"
								className="w-full"
								popoverContentClassName="pointer-events-auto"
								onRangeChange={(range: DateRange | undefined) => {
									if (range?.from && range?.to) {
										setEditedCampaign((prev) => ({
											...prev,
											campaign_window: {
												from: firebase.firestore.Timestamp.fromDate(range.from!),
												to: firebase.firestore.Timestamp.fromDate(range.to!)
											}
										}));
									} else {
										// Clear the campaign window when range is cleared
										setEditedCampaign((prev) => {
											const updated = { ...prev };
											delete updated.campaign_window;
											return updated;
										});
									}
								}}
							/>
						</div>
					</div>

					{/* Target Countries */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-blue-500/10 p-2">
								<Globe className="h-5 w-5 text-blue-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Países Objetivo *</h3>
						</div>

						<div className="grid grid-cols-1 gap-3">
							{COUNTRIES.map((country) => (
								<label
									key={country}
									className="group flex cursor-pointer items-center gap-3 rounded-xl border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-4 transition-all hover:border-neutral-700"
								>
									<Checkbox
										checked={editedCampaign.target?.countries?.includes(country) || false}
										onCheckedChange={() => toggleCountry(country)}
									/>
									<span className="text-sm font-medium capitalize text-neutral-200 group-hover:text-neutral-100">
										{COUNTRY_LABELS[country]}
									</span>
								</label>
							))}
						</div>
					</div>

					{/* Exclude Levels */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-amber-500/10 p-2">
								<Users className="h-5 w-5 text-amber-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Excluir Niveles (opcional)</h3>
						</div>

						<div className="grid grid-cols-2 gap-3">
							{LEVELS.map((level) => (
								<label
									key={level}
									className="group flex cursor-pointer items-center gap-3 rounded-xl border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-4 transition-all hover:border-neutral-700"
								>
									<Checkbox
										checked={editedCampaign.target?.exclude_levels?.includes(level) || false}
										onCheckedChange={() => toggleExcludeLevel(level)}
									/>
									<span className="text-sm font-medium text-neutral-200 group-hover:text-neutral-100">{levelLabels[level]}</span>
								</label>
							))}
						</div>
					</div>
				</div>

				{/* Actions */}
				<div className="fixed bottom-6 right-6 z-50">
					<div className="flex items-center gap-2 rounded-full border border-neutral-700 bg-neutral-900/90 p-2 shadow-2xl backdrop-blur-md">
						{!isNew && onDelete && (
							<Button
								loading={deleting}
								variant="secondary"
								onClick={handleDelete}
								className="h-10 rounded-full px-4 text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300"
							>
								<Trash2 className="h-4 w-4" />
								Eliminar
							</Button>
						)}
						<Button
							loading={saving}
							variant="primary"
							onClick={handleSave}
							className="h-10 rounded-full px-4 text-sm font-medium"
						>
							<Save className="h-4 w-4" />
							{isNew ? 'Crear' : 'Guardar'}
						</Button>
					</div>
				</div>
			</Dialog>
		</>
	);
}

export default function MarketingCampaigns() {
	const { internalCountry } = useContext(GlobalContext);
	const country = internalCountry?.get() || 'venezuela';

	const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [editingCampaign, setEditingCampaign] = useState<MarketingCampaign | null>(null);
	const [creatingCampaign, setCreatingCampaign] = useState(false);
	const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);
	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		const unsubscribe = firestore.collection('MarketingPopups').onSnapshot(
			(snapshot) => {
				try {
					const campaignsData: MarketingCampaign[] = [];

					snapshot.forEach((doc) => {
						campaignsData.push({
							id: doc.id,
							...doc.data()
						} as MarketingCampaign);
					});

					// Sort campaigns: active first, then by start date
					campaignsData.sort((a, b) => {
						const statusA = getCampaignStatus(a);
						const statusB = getCampaignStatus(b);

						// Priority: active > scheduled > expired > inactive
						const statusOrder = { active: 0, scheduled: 1, expired: 2, inactive: 3 };
						if (statusOrder[statusA] !== statusOrder[statusB]) {
							return statusOrder[statusA] - statusOrder[statusB];
						}

						// If same status, sort by start date (newest first)
						const aTime = a.campaign_window?.from?.seconds || 0;
						const bTime = b.campaign_window?.from?.seconds || 0;
						return bTime - aTime;
					});

					setCampaigns(campaignsData);
					setLoading(false);
				} catch (error) {
					console.error('Error processing marketing campaigns snapshot:', error);
					setAlertDialog({
						title: 'Error',
						message: 'Error al procesar las campañas de marketing'
					});
					setLoading(false);
				}
			},
			(error) => {
				console.error('Error listening to marketing campaigns:', error);
				setAlertDialog({
					title: 'Error',
					message: 'Error al cargar las campañas de marketing'
				});
				setLoading(false);
			}
		);

		// Cleanup subscription on unmount
		return () => unsubscribe();
	}, []);

	async function handleSaveCampaign(updatedCampaign: MarketingCampaign, originalId?: string) {
		setSaving(true);

		try {
			// Clean up undefined fields and mark empty strings for deletion
			const cleanedCampaign: any = { ...updatedCampaign };
			delete cleanedCampaign.id;

			Object.keys(cleanedCampaign).forEach((key) => {
				if (cleanedCampaign[key] === undefined) {
					delete cleanedCampaign[key];
				} else if (cleanedCampaign[key] === '') {
					// For updates, explicitly mark fields for deletion in Firestore
					if (originalId) {
						cleanedCampaign[key] = firebase.firestore.FieldValue.delete();
					} else {
						// For new documents, just remove empty strings
						delete cleanedCampaign[key];
					}
				}
			});

			// Ensure type is set
			cleanedCampaign.type = 'ON_STARTUP';

			// Ensure platforms is set inside target
			if (!cleanedCampaign.target) {
				cleanedCampaign.target = {};
			}
			if (!cleanedCampaign.target.platforms) {
				cleanedCampaign.target.platforms = ['mobile'];
			}

			if (originalId) {
				// Update existing campaign
				await firestore.collection('MarketingPopups').doc(originalId).update(cleanedCampaign);
			} else {
				// Create new campaign
				await firestore.collection('MarketingPopups').add(cleanedCampaign);
				setCreatingCampaign(false);
			}

			setEditingCampaign(null);
		} catch (error) {
			setAlertDialog({ title: 'Error', message: 'Error al guardar la campaña' });
			console.error('Error saving campaign:', error);
		} finally {
			setSaving(false);
		}
	}

	async function handleDeleteCampaign(campaignId: string) {
		setDeleting(true);

		try {
			await firestore.collection('MarketingPopups').doc(campaignId).delete();

			setEditingCampaign(null);
		} catch (error) {
			setAlertDialog({ title: 'Error', message: 'Error al eliminar la campaña' });
			console.error('Error deleting campaign:', error);
		} finally {
			setDeleting(false);
		}
	}

	const handleCreateCampaign = () => {
		const now = new Date();
		const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

		const newCampaign: MarketingCampaign = {
			type: 'ON_STARTUP',
			campaign_window: {
				from: firebase.firestore.Timestamp.fromDate(now),
				to: firebase.firestore.Timestamp.fromDate(nextWeek)
			},
			title: '',
			image_url: '',
			redirect_url: '',
			target: {
				countries: [],
				exclude_levels: [],
				platforms: ['mobile']
			},
			is_active: true
		};
		setEditingCampaign(newCampaign);
		setCreatingCampaign(true);
	};

	// Filter campaigns based on search query and country
	const filteredCampaigns = campaigns.filter((campaign) => {
		// First, check if the campaign targets the current country
		if (!campaign.target?.countries?.includes(country)) {
			return false;
		}

		// Then apply search query filter
		if (!searchQuery.trim()) return true;

		const query = searchQuery.toLowerCase();

		return (
			(campaign.title && campaign.title.toLowerCase().includes(query)) ||
			(campaign.image_url && campaign.image_url.toLowerCase().includes(query)) ||
			(campaign.redirect_url && campaign.redirect_url.toLowerCase().includes(query)) ||
			(campaign.target?.countries && campaign.target.countries.some((c) => c.toLowerCase().includes(query)))
		);
	});

	if (loading) {
		return <ContainerSpinner size={3} />;
	}

	return (
		<div className="mx-auto w-full max-w-7xl space-y-4 p-6">
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable={true}>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}
			{editingCampaign && (
				<EditCampaignDialog
					campaign={editingCampaign}
					onClose={() => {
						setEditingCampaign(null);
						setCreatingCampaign(false);
					}}
					onSave={handleSaveCampaign}
					onDelete={!creatingCampaign ? handleDeleteCampaign : undefined}
					saving={saving}
					deleting={deleting}
					isNew={creatingCampaign}
				/>
			)}

			{/* Search Bar */}
			<Input
				placeholder="Buscar campañas por título, URLs o países..."
				value={searchQuery}
				onChange={setSearchQuery}
				className="mb-4"
				Icon={<Search className="h-4 w-4 text-neutral-400" />}
			/>

			{/* Add New Campaign Card */}
			<div
				onClick={handleCreateCampaign}
				className="flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-neutral-600 bg-neutral-900/30 p-4 text-center transition-all duration-200 hover:border-primary-500 hover:bg-neutral-900/50 dark:border-neutral-700 dark:bg-neutral-900/20 dark:hover:border-primary-400 dark:hover:bg-neutral-900/40"
			>
				<div className="flex items-center gap-4">
					<div className="rounded-full bg-primary-500/10 p-3 dark:bg-primary-400/10">
						<Plus className="size-7 text-primary-500 dark:text-primary-400" />
					</div>
					<div>
						<h3 className="text-lg font-semibold text-neutral-200 dark:text-neutral-300">Agregar Campaña</h3>
						<p className="text-sm text-neutral-400 dark:text-neutral-500">Crear una nueva campaña de marketing</p>
					</div>
				</div>
			</div>

			{/* Main Content Container */}
			{filteredCampaigns.length === 0 && searchQuery.trim() ? (
				<div className="py-12 text-center">
					<Filter className="mx-auto h-12 w-12 text-neutral-400" />
					<h3 className="mt-4 text-lg font-semibold text-neutral-200">No se encontraron resultados</h3>
					<p className="text-neutral-400">No hay campañas que coincidan con tu búsqueda.</p>
				</div>
			) : filteredCampaigns.length === 0 ? (
				<div className="py-12 text-center">
					<ImageIcon className="mx-auto h-12 w-12 text-neutral-400" />
					<h3 className="mt-4 text-lg font-semibold text-neutral-200">No hay campañas</h3>
					<p className="text-neutral-400">Comienza creando tu primera campaña de marketing.</p>
				</div>
			) : (
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{filteredCampaigns.map((campaign, index) => {
						const status = getCampaignStatus(campaign);

						return (
							<div
								key={campaign.id || index}
								className="rounded-lg border border-neutral-900 bg-neutral-1000 p-4 dark:border-neutral-800 dark:bg-background"
							>
								<div className="mb-4 flex items-start justify-between gap-2">
									<div className="flex-grow">
										<h2 className="mb-1 truncate text-lg font-semibold text-primary-500 dark:text-primary-400">
											{campaign.title || 'Sin título'}
										</h2>
									</div>
									<div className="flex shrink-0 items-center gap-1">
										<button
											onClick={() => setEditingCampaign(campaign)}
											className="ml-2 flex flex-shrink-0 items-center rounded-full p-1 text-xs font-medium transition-colors hover:bg-neutral-800"
											title="Editar campaña"
										>
											<Pencil className="h-4 w-4 text-neutral-400" />
										</button>
										<span
											className={`flex flex-shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
												status === 'scheduled'
													? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
													: status === 'expired'
														? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
														: status === 'active'
															? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
															: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
											}`}
										>
											{status === 'scheduled' ? (
												<>
													<Clock className="mr-1 h-3 w-3" />
													Programada
												</>
											) : status === 'expired' ? (
												<>
													<XCircle className="mr-1 h-3 w-3" />
													Expirada
												</>
											) : status === 'active' ? (
												<>
													<CheckCircle className="mr-1 h-3 w-3" />
													Activa
												</>
											) : (
												<>
													<XCircle className="mr-1 h-3 w-3" />
													Inactiva
												</>
											)}
										</span>
									</div>
								</div>

								{/* Image Preview */}
								{campaign.image_url && (
									<div className="mb-3 overflow-hidden rounded-lg border border-neutral-800">
										<img
											src={campaign.image_url}
											alt={campaign.title || 'Campaign'}
											className="h-32 w-full object-cover"
											onError={(e) => {
												(e.target as HTMLImageElement).src =
													'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23333" width="100" height="100"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EError%3C/text%3E%3C/svg%3E';
											}}
										/>
									</div>
								)}

								<div className="space-y-3">
									{/* Campaign Period */}
									<div className="flex items-center text-sm">
										<Calendar className="mr-2 h-4 w-4 flex-shrink-0 text-primary-500" />
										<span className={`${campaign.campaign_window ? 'font-bold text-neutral-100 dark:text-neutral-200' : 'italic text-neutral-500 dark:text-neutral-500'}`}>
											{campaign.campaign_window?.from && campaign.campaign_window?.to
												? `${formatDate(campaign.campaign_window.from)} - ${formatDate(campaign.campaign_window.to)}`
												: 'No configurado'}
										</span>
									</div>

									{/* Image URL */}
									<div className="flex items-start text-sm">
										<ImageIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
										<div className="min-w-0 flex-1">
											<span className="font-medium text-neutral-100 dark:text-neutral-200">Imagen:&nbsp;</span>
											<a
												href={campaign.image_url}
												target="_blank"
												rel="noopener noreferrer"
												className="break-all text-blue-400 hover:text-blue-300 hover:underline"
											>
												{campaign.image_url.length > 40 ? campaign.image_url.substring(0, 40) + '...' : campaign.image_url}
											</a>
										</div>
									</div>

								{/* Redirect URL */}
								<div className="flex items-start text-sm">
									<LinkIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
									<div className="min-w-0 flex-1">
										<span className="font-medium text-neutral-100 dark:text-neutral-200">Redirección:&nbsp;</span>
										{campaign.redirect_url ? (
											<a
												href={campaign.redirect_url}
												target="_blank"
												rel="noopener noreferrer"
												className="break-all text-blue-400 hover:text-blue-300 hover:underline"
											>
												{campaign.redirect_url.length > 40
													? campaign.redirect_url.substring(0, 40) + '...'
													: campaign.redirect_url}
											</a>
										) : (
											<span className="italic text-neutral-500 dark:text-neutral-500">No configurada</span>
										)}
									</div>
								</div>

									{/* Target Countries */}
									<div>
										<div className="mb-1 flex items-center text-sm font-medium text-neutral-100 dark:text-neutral-200">
											<Globe className="mr-2 h-4 w-4 text-primary-500" />
											<span>Países:</span>
										</div>
										<div className="ml-6 flex flex-wrap gap-2">
											{campaign.target?.countries?.map((country, idx) => (
												<span
													key={idx}
													className="rounded-full bg-neutral-700 px-3 py-1 text-xs capitalize text-neutral-200 dark:bg-neutral-800 dark:text-neutral-300"
												>
													{country === 'rd' ? 'RD' : ucfirst(country)}
												</span>
											))}
										</div>
									</div>

									{/* Excluded Levels */}
									{campaign.target?.exclude_levels && campaign.target.exclude_levels.length > 0 && (
										<div>
											<div className="mb-1 flex items-center text-sm font-medium text-neutral-100 dark:text-neutral-200">
												<Users className="mr-2 h-4 w-4 text-amber-500" />
												<span>Niveles Excluidos:</span>
											</div>
											<div className="ml-6 flex flex-wrap gap-2">
												{campaign.target.exclude_levels.map((level, idx) => {
													const levelLabels: Record<(typeof LEVELS)[number], string> = {
														level_1: 'Azul',
														level_2: 'Plata',
														level_3: 'Oro',
														level_4: 'Platino'
													};
													return (
														<span
															key={idx}
															className="rounded-full bg-red-800/50 px-3 py-1 text-xs text-red-200 dark:bg-red-900/40 dark:text-red-300"
														>
															{levelLabels[level]}
														</span>
													);
												})}
											</div>
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}
